import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Footprints } from 'lucide-react';
import { Bar, BarChart, CartesianGrid, Cell, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { ernaehrungAPI } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

/* E9 Apple Health (15.10.2026): Schritte der letzten 28 Tage mit Ziel-Linie auf Ernährung › Mitglied. Zahlen kommen fertig vom
   Server (GET /api/ernaehrung/mitglied/{id}/schritte: Tage mit Quelle hand|healthkit, Ziel aus E1, Ø, erreicht, Trend) — hier
   wird nichts gerechnet. Dazu der Stand der Apple-Health-Einwilligung und des letzten Syncs; das Team kann einen erklärten
   Widerruf eintragen (löscht nichts, stoppt nur die Annahme). */

const TAGE = 28;
const fehlertext = (e, rest = 'Das hat nicht geklappt') => (typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : rest);
const tagKurz = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
const zeit = (iso) => (iso ? new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '–');
const QUELLE = { healthkit: 'Apple Health', hand: 'von Hand' };
const TREND = { auf: '↑ mehr als in der Woche davor', ab: '↓ weniger als in der Woche davor', gleich: '→ wie in der Woche davor' };

export function SchritteKarte({ leadId, darfBearbeiten = true, neuLadenBei = '' }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const laden = useCallback(() => ernaehrungAPI.schritte(leadId, TAGE).then((r) => setD(r.data)).catch(() => {}), [leadId]);
  useEffect(() => { laden(); }, [laden, neuLadenBei]);
  if (!d) return null;

  const akzent = (() => { try { return `hsl(${getComputedStyle(document.documentElement).getPropertyValue('--primary').trim()})`; } catch { return '#E60000'; } })();
  const leise = (() => { try { return `hsl(${getComputedStyle(document.documentElement).getPropertyValue('--muted-foreground').trim()})`; } catch { return '#6B7280'; } })();
  const reihe = d.tage.map((t) => ({ ...t, name: tagKurz(t.datum), fill: t.quelle === 'healthkit' ? akzent : t.quelle === 'hand' ? leise : 'transparent' }));
  const e = d.health?.einwilligung || {};
  const widerrufen = async () => {
    if (!window.confirm('Widerruf der Apple-Health-Einwilligung eintragen? Bereits übernommene Schritte bleiben, neue werden nicht mehr angenommen.')) return;
    setBusy(true);
    try { await ernaehrungAPI.healthWiderrufen(leadId); toast.success('Widerruf eingetragen'); laden(); }
    catch (err) { toast.error(fehlertext(err)); } finally { setBusy(false); }
  };

  return (
    <Card data-testid="schritte-karte">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2 flex-wrap"><Footprints className="w-4 h-4" /> Schritte
          <Badge variant="outline">Ziel {d.ziel.toLocaleString('de-DE')}</Badge>
          {d.tage_mit_wert > 0 && <Badge variant="outline" className={d.ziel_verfehlt_7_tage ? 'bg-amber-500/10 text-amber-700 border-amber-200' : ''}>{d.erreicht} von {TAGE} Tagen erreicht</Badge>}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        {d.tage_mit_wert === 0 ? (
          <p className="text-xs text-muted-foreground">Noch keine Schritte in den letzten {TAGE} Tagen — aus Apple Health (mit Einwilligung in der App) oder von Hand im Tagebuch.</p>
        ) : (
          <>
            <div className="h-36" aria-label={`Schritte der letzten ${TAGE} Tage, Ziel ${d.ziel}`}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={reihe} margin={{ top: 6, right: 4, bottom: 0, left: -18 }} barCategoryGap={2}>
                  <CartesianGrid vertical={false} stroke={leise} strokeOpacity={0.15} />
                  <XAxis dataKey="name" tick={{ fontSize: 10, fill: leise }} tickLine={false} axisLine={false} interval={6} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: leise }} tickLine={false} axisLine={false} width={44} tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : v)} />
                  <Tooltip formatter={(w, _n, p) => [`${Number(w).toLocaleString('de-DE')} Schritte${p?.payload?.quelle ? ` · ${QUELLE[p.payload.quelle] || p.payload.quelle}` : ''}`, '']}
                    labelFormatter={(l, p) => (p?.[0]?.payload?.datum ? new Date(`${p[0].payload.datum}T12:00:00`).toLocaleDateString('de-DE') : l)}
                    contentStyle={{ borderRadius: 12, fontSize: 12, border: '1px solid hsl(var(--border))', background: 'hsl(var(--card))', color: 'hsl(var(--foreground))' }} />
                  <ReferenceLine y={d.ziel} stroke={akzent} strokeDasharray="4 4" strokeOpacity={0.7} />
                  <Bar dataKey="schritte" radius={[3, 3, 0, 0]} isAnimationActive={false}>
                    {reihe.map((t) => <Cell key={t.datum} fill={t.fill} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
              <span>Ø <strong className="tabular-nums text-foreground">{d.schnitt.toLocaleString('de-DE')}</strong> je Tag</span>
              {d.trend && <span>{TREND[d.trend.richtung] || ''} (Ø {d.trend.aktuell.toLocaleString('de-DE')} zu {d.trend.davor.toLocaleString('de-DE')})</span>}
              <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-sm" style={{ background: akzent }} />Apple Health</span>
              <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-sm" style={{ background: leise }} />von Hand</span>
            </div>
          </>
        )}
        <div className="rounded-md border p-2 text-xs space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium">Apple Health</span>
            {e.erteilt ? <Badge variant="outline" className="bg-emerald-500/10 text-emerald-700 border-emerald-200">Einwilligung erteilt</Badge>
              : e.frueher ? <Badge variant="outline" className="bg-amber-500/10 text-amber-700 border-amber-200">ältere Fassung — neu zustimmen</Badge>
                : e.entschieden ? <Badge variant="outline">widerrufen</Badge> : <Badge variant="outline">keine Einwilligung</Badge>}
            {darfBearbeiten && (e.erteilt || e.frueher) && <button type="button" className="underline disabled:opacity-50" disabled={busy} onClick={widerrufen}>Widerruf eintragen</button>}
          </div>
          <p className="text-muted-foreground">
            {e.erteilt ? `Letzter Sync: ${zeit(d.health?.letzte_sync)}${d.health?.letzte_tage != null ? ` (${d.health.letzte_tage} Tage)` : ''}` : 'Das Mitglied schaltet Apple Health in der App frei (Profil › Datenschutz). Nur lesen: Schritte, Gewicht als Vorschlag im Check-in.'}
            {d.health?.gewicht_vorschlag && ` · Gewichtsvorschlag ${String(d.health.gewicht_vorschlag.kg).replace('.', ',')} kg vom ${new Date(`${d.health.gewicht_vorschlag.datum}T12:00:00`).toLocaleDateString('de-DE')} wartet auf den Check-in`}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
